import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef
} from '@angular/core';
import { ServerAuthUrlPipe } from 'auth';
import { I18nService, TranslatePipe } from 'i18n';
import { Observable, Subscription, take } from 'rxjs';
import { DownloadService, Logger } from 'shared';
import { MediaService } from '../../media.service';
import { MediaViewerOverlayRef } from '../media-overlay/media-viewer-overlay-ref';
import { MediaViewerOverlayService } from '../media-overlay/media-viewer-overlay.service';
import { Media, MediaDirectory, MediaFileEntry } from '../../models/media';

/** Icon drawn for an entry when it has no thumbnail of its own. */
export type MediaFileIcon = 'folder' | 'image' | 'pdf' | 'archive' | 'video' | 'audio' | 'document';

/** Lists one directory; `path` is relative to the area root. */
export type MediaExplorerSource = (path: string) => Observable<MediaDirectory>;

/** Context of the optional per-entry `#entryActions` template. */
export interface MediaExplorerEntryContext {
  readonly $implicit: MediaFileEntry;
  readonly entry: MediaFileEntry;
}

/**
 * File types the media viewer can show; everything else is downloaded. Video
 * is MP4 only: it is the one container every browser plays natively through
 * the html5 tech of video.js, and the only one the sales toolkit accepts.
 */
const VIEWER_TYPE_BY_EXTENSION: Record<string, 'image' | 'pdf' | 'video'> = {
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
  pdf: 'pdf',
  mp4: 'video'
};

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  pdf: 'application/pdf',
  // video.js picks its tech by this type - without it an .mp4 would be handed
  // to the HLS handler the player defaults to.
  mp4: 'video/mp4'
};

export interface MediaExplorerCrumb {
  readonly name: string;
  readonly path: string;
}

const ICON_BY_EXTENSION: Record<string, MediaFileIcon> = {
  pdf: 'pdf',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive', tgz: 'archive', bz2: 'archive',
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', bmp: 'image', avif: 'image', svg: 'image',
  mp4: 'video', mov: 'video', avi: 'video', mkv: 'video', webm: 'video', mpeg: 'video', mpg: 'video',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio', aac: 'audio', flac: 'audio'
};

/**
 * Windows-Explorer-style browser over a real directory of the media tree.
 *
 * Used for media items of type `explorer`, which name a folder on disk instead
 * of a single file. Directories open in place; images, PDFs and MP4 videos open
 * in the media viewer (look first, then download from there - what a sales toolkit is for),
 * every other file type is downloaded, because the folder may hold anything.
 *
 * The admin side reuses this component for the same look: it passes its own
 * listing `source`, per-entry actions as an `#entryActions` template, and calls
 * `reload()` after it changed the directory.
 */
@Component({
  selector: 'media-explorer',
  templateUrl: './media-explorer.component.html',
  styleUrls: ['./media-explorer.component.scss'],
  imports: [CommonModule, TranslatePipe, ServerAuthUrlPipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaExplorerComponent implements OnInit, OnDestroy {

  media = input.required<Media>();

  /** Name of the first breadcrumb; defaults to the media item's name. */
  rootName = input<string | undefined>(undefined);

  /**
   * Open images and PDFs in the media viewer instead of downloading them. A
   * flag rather than a fixed behaviour, so an explorer whose files are meant to
   * be taken away as-is can go back to download-only.
   */
  viewerForFiles = input<boolean>(true);

  /**
   * Where listings come from. Defaults to the member endpoint (MediaService.browse
   * with the item's language and area); the admin page passes its own endpoint,
   * which returns the identical listing shape.
   */
  source = input<MediaExplorerSource | undefined>(undefined);

  /**
   * Whether "up" at the root leaves the explorer (emits `exit`). A host that is
   * nothing but the explorer - the admin page - turns it off, so the button is
   * disabled there instead of being a control that does nothing.
   */
  exitable = input<boolean>(true);

  /** Raised when "up" is used at the explorer root - i.e. the user wants out. */
  exit = output<void>();

  /**
   * Raised whenever a file entry is opened, before the default action (viewer or
   * download) runs. Informational: the default action always happens.
   */
  entryOpen = output<MediaFileEntry>();

  /** Optional per-entry actions, e.g. rename/delete on the admin page. */
  protected readonly entryActions = contentChild<TemplateRef<MediaExplorerEntryContext>>('entryActions');

  protected readonly path = signal<string>('');
  protected readonly directory = signal<MediaDirectory | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly failed = signal<boolean>(false);

  /** Where browsing starts - also the point beyond which "up" is not offered. */
  protected readonly root = computed(() => {
    const media = this.media();
    return Media.isExplorer(media) ? media.properties?.explorer ?? null : null;
  });

  protected readonly entries = computed<MediaFileEntry[]>(() => this.directory()?.entries ?? []);

  /** The directory currently shown, relative to the area root. */
  readonly currentPath = this.path.asReadonly();

  protected readonly canGoUp = computed(() => {
    const root = this.root();
    return !!root && this.path() !== root.path;
  });

  protected readonly breadcrumb = computed<MediaExplorerCrumb[]>(() => {
    const root = this.root();
    if (!root) {
      return [];
    }
    const crumbs: MediaExplorerCrumb[] = [{ name: this.rootName() ?? this.media().name, path: root.path }];
    const relative = this.path().startsWith(root.path)
      ? this.path().slice(root.path.length).replace(/^\/+/, '')
      : '';
    let path = root.path;
    for (const segment of relative.split('/').filter(part => !!part)) {
      // A root of '' (a whole area, like the sales toolkit) must not turn the
      // crumbs into '/a/b' - that is not the path the listing hands out.
      path = path ? `${path}/${segment}` : segment;
      crumbs.push({ name: segment, path });
    }
    return crumbs;
  });

  private subscription?: Subscription;
  protected logger = new Logger('MediaExplorerComponent');

  constructor(private mediaService: MediaService,
              private mediaViewerOverlayService: MediaViewerOverlayService,
              private downloadService: DownloadService,
              private i18nService: I18nService) {
  }

  ngOnInit(): void {
    this.navigate(this.root()?.path ?? '');
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    // A viewer still open when the explorer goes (route change under it) must
    // not outlive it with a popstate listener nobody removes.
    this.openViewer?.destroy();
  }

  protected navigate(path: string): void {
    const root = this.root();
    if (!root) {
      this.failed.set(true);
      this.logger.warn('Explorer media without a directory', this.media());
      return;
    }
    this.path.set(path);
    this.loading.set(true);
    this.failed.set(false);
    this.subscription?.unsubscribe();
    this.subscription = this.list(root.area, path).subscribe({
      next: directory => {
        this.directory.set(directory);
        this.loading.set(false);
      },
      error: error => {
        this.logger.error('Failed to browse media directory', { path, error });
        this.directory.set(null);
        this.loading.set(false);
        this.failed.set(true);
      }
    });
  }

  private list(area: string, path: string): Observable<MediaDirectory> {
    const source = this.source();
    if (source) {
      return source(path);
    }
    // The listing must come from the same language directory the media item
    // itself was loaded from, not from whatever the UI happens to display.
    return this.mediaService.browse(this.language(), area, path);
  }

  private language(): string {
    return this.media().language || this.i18nService.getLanguage();
  }

  protected goUp(): void {
    // At the root there is nothing left to go up to inside the explorer, so the
    // button leaves it altogether - otherwise it would be a dead control and the
    // only way back would be the sidebar.
    if (!this.canGoUp()) {
      if (this.exitable()) {
        this.exit.emit();
      }
      return;
    }
    const parent = this.path().split('/').slice(0, -1).join('/');
    this.navigate(parent);
  }

  /** Re-reads the current directory, e.g. after the host changed its contents. */
  reload(): void {
    this.navigate(this.path());
  }

  protected onEntryClick(entry: MediaFileEntry, event: Event): void {
    // DownloadService downloads by appending a temporary <a download> to the
    // host element and clicking it. That synthetic click bubbles back into this
    // handler, so it must be ignored here - and above all not cancelled, since
    // preventDefault() on it aborts the download it is supposed to perform.
    if ((event.target as HTMLElement | null)?.closest('a')) {
      return;
    }
    if (entry.kind === 'directory') {
      this.navigate(entry.path);
      return;
    }
    this.entryOpen.emit(entry);
    if (this.viewerForFiles() && this.openInViewer(entry)) {
      return;
    }
    this.downloadFile(entry, event.currentTarget as HTMLElement);
  }

  /**
   * The download button on a file tile. Downloads straight away, whatever the
   * viewer would do with the file. The tile - not the button - is handed to
   * DownloadService as host: it appends its temporary <a download> there, which
   * may not sit inside a <button>, and the tile carries the "downloading" state.
   */
  protected onEntryDownload(entry: MediaFileEntry, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLElement;
    this.downloadFile(entry, (button.closest('.entry') as HTMLElement | null) ?? button);
  }

  private downloadFile(entry: MediaFileEntry, host: HTMLElement): void {
    if (!entry.src) {
      return;
    }
    this.downloadService
        .download(entry.src, host, {
          filename: entry.name,
          errorMessage: 'download.failed'
        })
        .catch(error => this.logger.warn('Failed to download file', { entry, error }));
  }

  /**
   * Shows an image, PDF or MP4 video in the media viewer, built as an ad-hoc
   * media item - the same shape a JSON-described image/pdf/video item has, so
   * the viewer needs no special case. Returns false for anything the viewer
   * cannot show.
   */
  private openInViewer(entry: MediaFileEntry): boolean {
    const extension = (entry.extension ?? '').toLowerCase();
    const type = VIEWER_TYPE_BY_EXTENSION[extension];
    // Always the file itself: `thumbnail` is a server-side preview now, not
    // the image, and the viewer is where the full resolution belongs.
    const src = this.inlineUrlOf(entry);
    if (!type || !src) {
      return false;
    }
    const mimeType = entry.mimeType || MIME_BY_EXTENSION[extension];
    const link = { src, mimeType, width: 0, height: 0 };
    const directory = this.directory();
    const media = {
      // The overlay service keys open viewers by id, so it must be unique per
      // file, not per explorer.
      id: `explorer:${directory?.area ?? this.root()?.area ?? ''}:${entry.path}`,
      type,
      name: entry.name,
      language: directory?.language || this.language(),
      cover: this.coverOf(type, link, entry),
      links: [link],
      properties: { download: true }
    } as Media;
    this.mediaViewerOverlayService
        .open({ media })
        .then(ref => this.bindViewerToHistory(ref))
        .catch(error => this.logger.warn('Failed to open media viewer', { entry, error }));
    return true;
  }

  /**
   * The cover of the ad-hoc item. An image is its own cover; a video gets the
   * server frame the tile shows as its poster (the player resolves the
   * root-relative URL against the API exactly as the tile's serverAuthUrl pipe
   * does), or none - an empty src means "no poster" to the player, so a video
   * whose frame could not be made still plays, just on black.
   */
  private coverOf(type: 'image' | 'pdf' | 'video', link: { src: string; mimeType: string; width: number; height: number },
                  entry: MediaFileEntry) {
    if (type === 'image') {
      return link;
    }
    const poster = type === 'video' && entry.thumbnail && !this.thumbnailFailed(entry) ? entry.thumbnail : '';
    return { src: poster, mimeType: poster ? 'image/jpeg' : link.mimeType, width: 0, height: 0 };
  }

  private openViewer?: MediaViewerOverlayRef;

  /**
   * Makes the browser's Back button close the viewer instead of leaving the
   * explorer. The media page does the same for its own items by pushing the
   * item id onto the URL; that is not possible here, because the page reads
   * the last URL segment as a media id and would drop the explorer for an id
   * it cannot resolve. So the viewer gets a history entry with the SAME url and
   * only a state marker: Back pops it (popstate → destroy), the Close button
   * pops it for the user (destroyed with the marker still on top → history.back),
   * and the router ignores the popstate as a same-url navigation.
   */
  private bindViewerToHistory(ref: MediaViewerOverlayRef): void {
    this.openViewer = ref;
    const token = `media-explorer:${Date.now()}`;
    history.pushState({ ...(history.state ?? {}), mediaExplorerViewer: token }, '', location.href);
    const onPopState = () => {
      if (history.state?.mediaExplorerViewer !== token) {
        window.removeEventListener('popstate', onPopState);
        ref.destroy();
      }
    };
    window.addEventListener('popstate', onPopState);
    ref.onDestroyed.pipe(take(1)).subscribe(() => {
      window.removeEventListener('popstate', onPopState);
      if (this.openViewer === ref) {
        this.openViewer = undefined;
      }
      if (history.state?.mediaExplorerViewer === token) {
        history.back();
      }
    });
  }

  /**
   * Inline URL of a file, `/media/<language>/<area>/<path>` - the route images
   * and PDFs of JSON media items are served from, and what the backend already
   * hands out as `thumbnail` for images. Built here for PDFs, which have none.
   */
  private inlineUrlOf(entry: MediaFileEntry): string | null {
    const area = this.directory()?.area ?? this.root()?.area;
    if (!area || !entry.path) {
      return null;
    }
    const language = this.directory()?.language || this.language();
    const path = entry.path.split('/').filter(segment => !!segment).map(encodeURIComponent).join('/');
    return `/media/${encodeURIComponent(language)}/${encodeURIComponent(area)}/${path}`;
  }

  /** Keyboard activation - here preventDefault only stops the page scrolling. */
  protected onEntryKeydown(entry: MediaFileEntry, event: Event): void {
    event.preventDefault();
    this.onEntryClick(entry, event);
  }

  /** Entries whose preview failed to load - shown with their type icon instead. */
  private readonly failedThumbnails = signal<ReadonlySet<string>>(new Set());

  protected thumbnailFailed(entry: MediaFileEntry): boolean {
    return this.failedThumbnails().has(entry.path);
  }

  protected onThumbnailError(entry: MediaFileEntry): void {
    this.failedThumbnails.update(failed => new Set(failed).add(entry.path));
  }

  protected iconOf(entry: MediaFileEntry): MediaFileIcon {
    if (entry.kind === 'directory') {
      return 'folder';
    }
    return ICON_BY_EXTENSION[entry.extension ?? ''] ?? 'document';
  }

  /** Short type label drawn on the icon sheet, like a file manager does. */
  protected labelOf(entry: MediaFileEntry): string {
    return (entry.extension ?? '').slice(0, 4).toUpperCase();
  }

  protected formatSize(size?: number): string {
    if (size === undefined || size === null) {
      return '';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = size;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }
    return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
  }

  protected trackByPath = (_: number, entry: MediaFileEntry) => entry.path;
}
